import { ApiError } from '@api/client'
import { TREE_CONDITIONS, type TreeCondition } from '@api/types'
import type { Channel } from '@features/tree/channel'
import { useI18n } from '@i18n/I18nProvider'
import { getCurrentPosition, type Position } from '@lib/geolocation'
import { Button } from '@ui/Button'
import { CONDITION_STYLES } from '@ui/ConditionBadge'
import { Icon } from '@ui/Icon'
import { type ChangeEvent, type FormEvent, type ReactNode, useEffect, useState } from 'react'
import { useReportTree } from './useReportTree'

type LocationState = { status: 'idle' | 'locating' | 'unavailable' } | { status: 'added'; position: Position }

type Props = {
	code: string
	channel: Channel
	// Paso a paso: primero solo los cuatro estados; al elegir uno aparecen foto, ubicación y Enviar
	// (hoja del mapa y ficha del árbol). Si no, todo a la vez (pantalla de aviso).
	stepwise?: boolean
	// Texto de la pregunta; oculto si quien lo usa ya lo enseña como título.
	legend?: string
	hideLegend?: boolean
	// Qué ofrecer tras enviar (p. ej. "Volver al árbol").
	doneAction?: ReactNode
}

// Aviso sobre el estado de un árbol (spec `tree-reports`): cuatro estados grandes, foto y
// ubicación opcionales. Pensado para una mano y poca cobertura. Al enviarlo da las gracias en el
// mismo sitio, sin cambiar de pantalla.
export const ReportForm = ({ code, channel, stepwise = false, legend, hideLegend = false, doneAction }: Props) => {
	const { t } = useI18n()
	const report = useReportTree(code)
	const [condition, setCondition] = useState<TreeCondition | null>(null)
	const [photo, setPhoto] = useState<File | null>(null)
	const [preview, setPreview] = useState<string | null>(null)
	const [location, setLocation] = useState<LocationState>({ status: 'idle' })

	useEffect(() => {
		if (!photo) {
			setPreview(null)
			return
		}
		const url = URL.createObjectURL(photo)
		setPreview(url)
		return () => URL.revokeObjectURL(url)
	}, [photo])

	const onPhoto = (event: ChangeEvent<HTMLInputElement>) => setPhoto(event.target.files?.[0] ?? null)

	const locate = async () => {
		setLocation({ status: 'locating' })
		const position = await getCurrentPosition()
		setLocation(position ? { status: 'added', position } : { status: 'unavailable' })
	}

	const submit = (event: FormEvent) => {
		event.preventDefault()
		if (!condition) return
		report.mutate({
			condition,
			channel,
			photo,
			location: location.status === 'added' ? location.position : null,
		})
	}

	if (report.isSuccess)
		return (
			<div role="status" className="flex flex-col items-center gap-3 py-4 text-center">
				<span className="flex size-16 items-center justify-center rounded-full bg-healthy text-white">
					<Icon name="check" className="size-8" />
				</span>
				<p className="text-2xl font-semibold">{t('report.thanks')}</p>
				<p className="text-lg">{report.data.replaced ? t('report.replaced') : t('report.thanksDetail')}</p>
				{doneAction}
			</div>
		)

	const errorMessage =
		report.error instanceof ApiError && report.error.offline
			? t('common.offline')
			: report.error
				? t('report.error')
				: null
	const showDetails = !stepwise || condition !== null

	return (
		<form onSubmit={submit} className="flex flex-col gap-6">
			<fieldset>
				<legend className={hideLegend ? 'sr-only' : 'mb-3 text-lg font-semibold'}>
					{legend ?? t('report.condition')}
				</legend>
				<div className="grid grid-cols-2 gap-3">
					{TREE_CONDITIONS.map((option) => {
						const selected = condition === option
						return (
							<button
								key={option}
								type="button"
								aria-pressed={selected}
								onClick={() => setCondition(option)}
								className={`flex min-h-20 items-center gap-3 rounded-2xl border-2 px-4 text-left text-lg font-semibold ${selected ? 'border-forest-800 bg-forest-50' : 'border-earth-300 bg-white'}`}
							>
								<span
									className={`size-5 shrink-0 rounded-full ${CONDITION_STYLES[option]}`}
									aria-hidden="true"
								/>
								{t(`condition.${option}`)}
								{selected && <Icon name="check" className="ml-auto size-6 text-forest-800" />}
							</button>
						)
					})}
				</div>
			</fieldset>

			{showDetails && (
				<>
					<div>
						{preview ? (
							<div className="flex items-center gap-4 rounded-2xl bg-white p-3">
								<img
									src={preview}
									alt={t('report.photoSelected')}
									className="size-20 rounded-xl object-cover"
								/>
								<span className="font-semibold">{t('report.photoSelected')}</span>
								<Button variant="ghost" className="ml-auto" onClick={() => setPhoto(null)}>
									{t('report.removePhoto')}
								</Button>
							</div>
						) : (
							<label className="flex min-h-14 cursor-pointer items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-forest-500 bg-white px-4 font-semibold text-forest-800">
								<Icon name="camera" />
								{t('report.photo')}
								<input
									type="file"
									accept="image/*"
									capture="environment"
									className="sr-only"
									onChange={onPhoto}
								/>
							</label>
						)}
					</div>

					<div>
						{location.status === 'added' ? (
							<p className="flex min-h-12 items-center gap-2 font-semibold text-healthy">
								<Icon name="pin" />
								{t('report.locationAdded')}
							</p>
						) : (
							<Button
								variant="secondary"
								fullWidth
								onClick={locate}
								disabled={location.status === 'locating'}
							>
								<Icon name="pin" />
								{location.status === 'locating' ? t('common.loading') : t('report.location')}
							</Button>
						)}
						{location.status === 'unavailable' && (
							<p className="mt-2 text-earth-800">{t('report.locationUnavailable')}</p>
						)}
					</div>

					<p className="text-sm text-earth-800">{t('report.privacy')}</p>

					{errorMessage && (
						<p role="alert" className="rounded-2xl bg-damaged/10 p-4 font-semibold text-damaged">
							{errorMessage}
						</p>
					)}

					<Button type="submit" fullWidth disabled={!condition || report.isPending}>
						{report.isPending ? t('report.sending') : t('report.submit')}
					</Button>
					{!condition && (
						<p className="-mt-3 text-center text-sm text-earth-800">{t('report.chooseCondition')}</p>
					)}
				</>
			)}
		</form>
	)
}
